import { useEffect, useState, useCallback } from "react";
import {
  getVendors,
  createVendor,
  deleteVendor,
  updateVendor
} from "../../api/adminApi";
import { useNavigate, useParams } from "react-router-dom";

import {
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  CircularProgress,
  Chip,
  Box,
  TableContainer,
  Stack,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Divider,
  IconButton
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import EditLocationIcon from "@mui/icons-material/EditLocation";

const modernInputStyle = {
  '& .MuiInputLabel-root': { color: '#A59D96', fontWeight: '600' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#D97706' },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px', bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: '#ECE5DA' },
    '&:hover fieldset': { borderColor: '#F29D17' },
    '&.Mui-focused fieldset': { borderColor: '#F8C332', borderWidth: '2px' }
  },
  '& .MuiOutlinedInput-input': { fontWeight: '700', color: '#2F2A25' }
};

export default function Vendors({ zonalAdminId }) {
  const navigate = useNavigate();

  const { id: urlZonalAdminId } = useParams();
  const activeAdminId = zonalAdminId || urlZonalAdminId;

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const [editId, setEditId] = useState(null);

  const [useLiveLocation, setUseLiveLocation] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const [form, setForm] = useState({
    name: "", location: "", lat: "", lng: "", email: "", password: "",
  });

  const handleBack = () => {
    if (urlZonalAdminId) {
      navigate(`/zonal-admins/${urlZonalAdminId}`);
    } else {
      navigate("/dashboard");
    }
  };

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const params = activeAdminId ? { zonal_admin_id: activeAdminId } : {};
      const res = await getVendors(params);
      const vendorArray = Array.isArray(res.data) ? res.data : (res.data?.vendors || []);
      setVendors(vendorArray);
    } catch (err) {
      alert("Failed to load vendors");
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, [activeAdminId]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const fetchLiveLocation = () => {
    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((prev) => ({ ...prev, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        setFetchingLocation(false);
      },
      () => {
        alert("Failed to get location");
        setFetchingLocation(false);
      }
    );
  };

  const handleEdit = (v) => {
    setEditId(v.id);
    setForm({ name: v.name, location: v.location || "", lat: v.lat, lng: v.lng, email: v.email || "", password: "" });
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.lat || !form.lng) {
      alert("Latitude & Longitude required");
      return;
    }

    try {
      const payload = { ...form };
      if (activeAdminId) {
        payload.zonal_admin_id = activeAdminId;
      }

      if (editId) {
        await updateVendor(editId, payload);
      } else {
        await createVendor(payload);
      }

      setOpen(false);
      resetForm();
      fetchVendors();
    } catch (err) {
      alert(err?.response?.data?.error || "Failed");
    }
  };

  const handleDeleteClick = (vendor) => {
    setVendorToDelete(vendor);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!vendorToDelete) return;
    try {
      await deleteVendor(vendorToDelete.id);
      setDeleteDialogOpen(false);
      setVendorToDelete(null);
      fetchVendors();
    } catch (err) {
      alert("Failed to delete vendor");
    }
  };

  const resetForm = () => {
    setForm({ name: "", location: "", lat: "", lng: "", email: "", password: "" });
    setUseLiveLocation(false);
    setEditId(null);
  };

  const getReturnUrl = () => activeAdminId ? `/zonal-admins/${activeAdminId}/vendors` : '/vendors';

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1200, mx: "auto", minHeight: "100vh", bgcolor: "#FFFDF8" }}>
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={2} mb={4}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton onClick={handleBack} sx={{ bgcolor: '#FFFFFF', border: '1px solid #ECE5DA', color: '#3A2C23', '&:hover': { bgcolor: '#F8C332', borderColor: '#F8C332' } }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", letterSpacing: "-0.5px" }}>Vendor Management</Typography>
        </Box>

        <Button
          variant="contained" size="large" disableElevation
          onClick={() => { setOpen(true); resetForm(); }}
          sx={{
            borderRadius: '16px', textTransform: "none", fontWeight: 900, bgcolor: '#F8C332', color: '#3A2C23',
            boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)', '&:hover': { bgcolor: '#F5B71D' },
            py: { xs: 1.5, sm: 1.2 }, px: 3, width: { xs: "100%", sm: "auto" }
          }}
        >
          + Add Vendor
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress size={60} thickness={4} sx={{ color: '#F8C332' }} /></Box>
      ) : (
        <>
          {isMobile ? (
            <Stack spacing={2}>
              {vendors?.map((v) => (
                <Card
                  key={v.id} elevation={0}
                  sx={{
                    borderRadius: '20px', border: "none", bgcolor: '#FFFFFF',
                    boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15)' }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography
                        variant="h6" sx={{ color: "#E85D3F", cursor: "pointer", fontWeight: 900 }}
                        onClick={() => navigate(`/vendors/${v.id}`, { state: { returnUrl: getReturnUrl() } })}
                      >
                        {v.name}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1.5, borderStyle: 'dashed', borderColor: '#ECE5DA' }} />
                    <Stack spacing={1.5}>
                      <Typography variant="body2" color="#6E655E" fontWeight="600"><strong style={{ color: '#3A2C23' }}>Zone:</strong> {v.zone}</Typography>
                      <Box display="flex" gap={1}>
                        <Chip label={v.is_active ? "Active" : "Inactive"} color={v.is_active ? "success" : "default"} size="small" variant={v.is_active ? "filled" : "outlined"} sx={{ fontWeight: 800, borderRadius: '8px' }} />
                        <Chip label={v.is_open ? "OPEN" : "CLOSED"} color={v.is_open ? "success" : "error"} size="small" sx={{ fontWeight: 800, borderRadius: '8px' }} />
                      </Box>
                      <Box mt={1} pt={2} borderTop="1px dashed #ECE5DA" display="flex" justifyContent="flex-end" gap={1}>
                        <IconButton sx={{ color: '#F29D17', bgcolor: '#FFF6DE', '&:hover': { bgcolor: '#F8C332', color: '#3A2C23' } }} onClick={() => handleEdit(v)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton sx={{ color: '#E85D3F', bgcolor: '#FFF0EE', '&:hover': { bgcolor: '#E85D3F', color: '#FFF' } }} onClick={() => handleDeleteClick(v)}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {vendors.length === 0 && (
                <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: '20px', border: "1px dashed #ECE5DA", bgcolor: '#FFFFFF' }}>
                  <Typography color="text.secondary" fontWeight="700">No vendors found for this zone.</Typography>
                </Paper>
              )}
            </Stack>
          ) : (
            <TableContainer
              component={Paper} elevation={0}
              sx={{
                borderRadius: '20px', border: "none", bgcolor: '#FFFFFF',
                boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15)' }
              }}
            >
              <Table sx={{ minWidth: 800 }}>
                <TableHead sx={{ bgcolor: "#FFFDF8" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Zone</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Kitchen</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#A59D96' }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendors?.map((v) => (
                    <TableRow key={v.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell>
                        <Typography
                          variant="body2" fontWeight="900"
                          sx={{ color: '#E85D3F', cursor: "pointer", '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => navigate(`/vendors/${v.id}`, { state: { returnUrl: getReturnUrl() } })}
                        >
                          {v.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#3A2C23' }}>{v.zone}</TableCell>
                      <TableCell><Chip label={v.is_active ? "Active" : "Inactive"} color={v.is_active ? "success" : "default"} size="small" variant={v.is_active ? "filled" : "outlined"} sx={{ fontWeight: 800, borderRadius: '6px' }} /></TableCell>
                      <TableCell><Chip label={v.is_open ? "OPEN" : "CLOSED"} color={v.is_open ? "success" : "error"} size="small" sx={{ fontWeight: 800, borderRadius: '6px' }} /></TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton size="small" sx={{ color: '#F29D17', bgcolor: '#FFF6DE', '&:hover': { bgcolor: '#F8C332', color: '#3A2C23' }, mr: 1 }} onClick={() => handleEdit(v)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" sx={{ color: '#E85D3F', bgcolor: '#FFF0EE', '&:hover': { bgcolor: '#E85D3F', color: '#FFF' } }} onClick={() => handleDeleteClick(v)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {vendors.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary', fontWeight: 700 }}>No vendors found for this zone.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog
        open={open} onClose={() => { setOpen(false); resetForm(); }} fullWidth maxWidth="sm" fullScreen={fullScreenDialog}
        PaperProps={{ sx: { borderRadius: fullScreenDialog ? 0 : '24px', bgcolor: '#FFFDF8', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' } }}
      >
        <DialogTitle sx={{ fontWeight: 900, pb: 2, pt: 3, px: { xs: 2, sm: 4 }, color: '#3A2C23', borderBottom: '1px dashed #ECE5DA' }}>
          {editId ? "Edit Vendor" : "Add Vendor"}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 4 }, pt: "24px !important", pb: { xs: '15vh', sm: 4 } }}>
          <Stack spacing={2.5}>
            <TextField sx={modernInputStyle} label="Name" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField sx={modernInputStyle} label="Location" fullWidth value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} pt={1}>
              <Button fullWidth disableElevation startIcon={<MyLocationIcon />} variant={useLiveLocation ? "contained" : "outlined"} onClick={() => { setUseLiveLocation(true); fetchLiveLocation(); }} sx={{ borderRadius: '12px', fontWeight: 800, textTransform: 'none', py: 1.2, ...(useLiveLocation ? { bgcolor: '#DDECC8', color: '#577A34', '&:hover': { bgcolor: '#CCE3AF' } } : { borderColor: '#ECE5DA', color: '#6E655E' }) }}>Live Location</Button>
              <Button fullWidth disableElevation startIcon={<EditLocationIcon />} variant={!useLiveLocation ? "contained" : "outlined"} onClick={() => setUseLiveLocation(false)} sx={{ borderRadius: '12px', fontWeight: 800, textTransform: 'none', py: 1.2, ...(!useLiveLocation ? { bgcolor: '#F8C332', color: '#3A2C23', '&:hover': { bgcolor: '#F5B71D' } } : { borderColor: '#ECE5DA', color: '#6E655E' }) }}>Manual</Button>
            </Stack>
            {fetchingLocation && (
              <Box display="flex" alignItems="center" gap={2} pt={1}>
                <CircularProgress size={20} sx={{ color: '#F8C332' }} />
                <Typography variant="body2" fontWeight="700" color="#6E655E">Fetching location...</Typography>
              </Box>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
              <TextField sx={modernInputStyle} label="Latitude" fullWidth value={form.lat} disabled={useLiveLocation} onChange={(e) => setForm({ ...form, lat: parseFloat(e.target.value) })} />
              <TextField sx={modernInputStyle} label="Longitude" fullWidth value={form.lng} disabled={useLiveLocation} onChange={(e) => setForm({ ...form, lng: parseFloat(e.target.value) })} />
            </Stack>
            <TextField sx={modernInputStyle} label="Email" fullWidth value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <TextField sx={modernInputStyle} label="Password" type="password" fullWidth value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, px: { sm: 4 }, borderTop: "1px solid #ECE5DA", bgcolor: '#FFFFFF', borderRadius: '0 0 24px 24px' }}>
          <Button onClick={() => { setOpen(false); resetForm(); }} color="inherit" sx={{ textTransform: "none", fontWeight: 800, color: '#6E655E', borderRadius: '12px', px: 3 }}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={handleSubmit} sx={{ textTransform: "none", fontWeight: 900, borderRadius: '12px', bgcolor: '#F8C332', color: '#3A2C23', px: 4, py: 1.2, boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)', '&:hover': { bgcolor: '#F5B71D' } }}>{editId ? "Update" : "Create"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px', bgcolor: '#FFFDF8' } }}>
        <DialogTitle sx={{ fontWeight: 900, color: '#3A2C23' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#6E655E", fontWeight: 600 }}>Are you sure you want to delete the vendor <Typography component="span" fontWeight="900" color="#E85D3F">{vendorToDelete?.name}</Typography>?</DialogContentText>
          <DialogContentText sx={{ color: "#DC2626", mt: 2, fontSize: "0.875rem", fontWeight: 700 }}>This action is permanent and cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight: 800, color: '#6E655E', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" disableElevation sx={{ fontWeight: 800, borderRadius: '10px', textTransform: 'none', bgcolor: '#DC2626', color: '#FFFFFF', '&:hover': { bgcolor: '#B91C1C' } }}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}